import { Button, LinkButton, Tag, Textarea, useAlert } from '@ads/components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Skeleton } from '@/shared/ui/atoms';

import type { TutorPromptFieldState } from '../../../hooks/usePromptDraft';
import { PROMPT_MAX_LENGTH } from '../../../utils/tutorAiLimits';
import { ConfirmDialog } from '../../molecules/ConfirmDialog';
import { SurfaceCard } from '../../molecules/SurfaceCard';

/**
 * Props do `PromptConfigForm`.
 *
 * O componente não sabe se está editando persona ou memória, nem se o escopo é global ou
 * de turma — todo esse contexto chega pronto via prop, para o mesmo organismo servir os
 * quatro casos sem variante.
 */
export interface PromptConfigFormProps {
  /** Título do card (ex.: "Persona" ou "Memória"). */
  readonly title: string;
  /** Descrição exibida sob o título, explicando o que este campo representa. */
  readonly description: string;
  /** Rótulo do textarea de edição. */
  readonly label: string;
  /** Texto de exemplo exibido no textarea vazio. */
  readonly placeholder: string;
  /**
   * Corpo do diálogo de confirmação exibido antes de salvar.
   *
   * Muda conforme o escopo de quem monta o formulário: a versão global avisa que a
   * mudança vale para todas as turmas, a de turma avisa que sobrescreve a configuração
   * global só ali.
   */
  readonly confirmBody: string;
  /**
   * Mensagem exibida quando não há conteúdo próprio configurado neste escopo
   * (`state.isInherited`).
   *
   * Muda conforme o escopo de quem monta o formulário: no global não existe "herança" —
   * o global É a configuração — então a mensagem é de "nada configurado ainda"
   * (`config.notConfigured`); na turma, o texto correto é o de herança do global
   * (`config.inherited`).
   */
  readonly inheritedMessage: string;
  /**
   * Estado do campo de prompt (valor persistido, rascunho e ações de salvar/descartar),
   * produzido por `useTutorPersonaConfig` ou `useTutorMemoryConfig` no escopo desejado.
   */
  readonly state: TutorPromptFieldState;
}

/**
 * Editor de um campo de prompt do tutor (persona ou memória) num escopo.
 *
 * Salvar passa por confirmação explícita porque a mudança afeta as respostas do
 * assistente na hora e a API não guarda histórico. O componente é agnóstico de escopo e
 * de qual prompt está editando — quem monta decide, o que permite usá-lo quatro vezes
 * (persona/memória × global/turma) sem variante.
 */
export function PromptConfigForm({
  title,
  description,
  label,
  placeholder,
  confirmBody,
  inheritedMessage,
  state,
}: PromptConfigFormProps) {
  const { t } = useTranslation('tutorAI');
  const { alert } = useAlert();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const remaining = PROMPT_MAX_LENGTH - state.draft.length;

  function handleConfirm() {
    state.save({
      onSuccess: () => alert({ alertType: 'success', title: t('config.saved') }),
      onError: () => alert({ alertType: 'danger', title: t('config.saveError') }),
    });
    setIsConfirmOpen(false);
  }

  return (
    <>
      <SurfaceCard
        title={title}
        description={description}
        headerRight={
          state.isDirty ? (
            <Tag
              label={t('config.unsavedBadge')}
              size="sm"
              status="highlight"
              custom={{ text: 'text-nowrap' }}
            />
          ) : null
        }
      >
        <div className="flex flex-col gap-mini p-xxs">
          {state.isError ? (
            <Alert type="danger">
              <div className="flex w-full flex-wrap items-center justify-between gap-mini">
                <span>{t('config.loadError')}</span>
                <Button hierarchy="secondary" size="sm" onClick={state.refetch}>
                  {t('config.retry')}
                </Button>
              </div>
            </Alert>
          ) : (
            <>
              {state.isLoading ? (
                <Skeleton className="h-xxl w-full" />
              ) : (
                <Textarea
                  label={label}
                  placeholder={placeholder}
                  value={state.draft}
                  onChange={event => state.setDraft(event.target.value)}
                  maxLength={PROMPT_MAX_LENGTH}
                  rows={12}
                  fullWidth
                  resizable
                  custom={{ textarea: 'font-mono' }}
                />
              )}

              {state.isInherited ? (
                <p className="text-ctx-content-placeholder ts-caption-xxs">{inheritedMessage}</p>
              ) : null}

              <div className="flex flex-col gap-nano sm:flex-row sm:items-center sm:justify-between">
                <span className="text-ctx-content-placeholder ts-caption-xxs">
                  {t('config.charactersRemaining', { count: remaining })}
                </span>

                <div className="flex items-center gap-xxs">
                  {state.isDirty ? (
                    <LinkButton hierarchy="secondary" size="sm" onClick={state.discard}>
                      {t('config.discard')}
                    </LinkButton>
                  ) : null}
                  <Button
                    hierarchy="primary"
                    size="md"
                    disabled={!state.isDirty}
                    isLoading={state.isSaving}
                    onClick={() => setIsConfirmOpen(true)}
                  >
                    {t('config.save')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </SurfaceCard>

      <ConfirmDialog
        open={isConfirmOpen}
        title={t('config.confirm.title')}
        body={confirmBody}
        cancelLabel={t('config.confirm.cancel')}
        confirmLabel={t('config.confirm.confirm')}
        onConfirm={handleConfirm}
        onOpenChange={setIsConfirmOpen}
      />
    </>
  );
}
